import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { fetchAPI } from '../../utils/api';
import {
    FaUserCircle, FaSave, FaKey,
    FaIdCard, FaGraduationCap, FaMapPin,
    FaLock, FaDownload
} from 'react-icons/fa';
import { QRCodeSVG } from 'qrcode.react';
import { motion, AnimatePresence } from 'framer-motion';
import { encryptQR } from '../../utils/security';
import { PasswordStrengthIndicator, validatePassword } from '../../utils/passwordValidation';
import { jsPDF } from "jspdf";

export default function Profile() {
    const { currentUser, changePassword } = useAuth();
    const [profileData, setProfileData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [institutions, setInstitutions] = useState([]);
    const [formData, setFormData] = useState({
        name: '', email: '', phone: '', birthday: '', age: '',
        address: '', school_name: '', grade: '', stream: '',
        guardian_name: '', guardian_contact: '',
        address_line1: '', address_line2: '', district: '', city: '',
        institution: '', medium: 'Sinhala'
    });
    const [passwordData, setPasswordData] = useState({ current: '', new: '', confirm: '' });
    const [message, setMessage] = useState({ type: '', text: '' });
    const [activeTab, setActiveTab] = useState('personal');
    const qrRef = useRef(null);

    // Download ID Card as PDF (4" × 2")
    const downloadIDCard = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // High Quality Scale (e.g., 4x for ~300 DPI quality equivalent)
        const scale = 4;
        const width = 384 * scale;
        const height = 192 * scale;
        canvas.width = width;
        canvas.height = height;

        // Scale context to draw using original expected coordinates
        ctx.scale(scale, scale);

        // Original Dimensions for drawing logic
        const cardWidth = 384;
        const cardHeight = 192;

        // Create gradient background
        const gradient = ctx.createLinearGradient(0, 0, cardWidth, cardHeight);
        gradient.addColorStop(0, '#1e3a8a'); // Blue-900
        gradient.addColorStop(0.5, '#2563eb'); // Blue-600
        gradient.addColorStop(1, '#60a5fa'); // Blue-400
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, cardWidth, cardHeight);

        // Add decorative circles
        ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
        ctx.beginPath();
        ctx.arc(50, 50, 60, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(cardWidth - 30, cardHeight - 30, 40, 0, Math.PI * 2);
        ctx.fill();

        // Add white card overlay
        ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
        ctx.fillRect(10, 10, cardWidth - 20, cardHeight - 20);

        // Get QR code as data URL
        const qrSvg = qrRef.current?.querySelector('svg');
        if (qrSvg) {
            const svgData = new XMLSerializer().serializeToString(qrSvg);
            const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(svgBlob);
            const qrImage = new Image();

            qrImage.onload = () => {
                // Draw QR code on the left side with white background
                const qrSize = 130;
                const qrX = 25;
                const qrY = (cardHeight - qrSize) / 2;

                // QR Background
                ctx.fillStyle = 'white';
                ctx.fillRect(qrX - 5, qrY - 5, qrSize + 10, qrSize + 10);
                // Draw QR
                ctx.drawImage(qrImage, qrX, qrY, qrSize, qrSize);

                // Draw text information on the right side
                const textX = qrX + qrSize + 25;

                // Title
                ctx.fillStyle = 'white';
                ctx.font = 'bold 16px Arial';
                ctx.fillText('STUDENT IDENTITY', textX, 40);

                // Student Name
                ctx.font = 'bold 14px Arial';
                ctx.fillText(profileData?.name || 'N/A', textX, 65);

                // Index Number
                ctx.font = 'bold 12px Arial';
                ctx.fillStyle = '#fbbf24'; // Amber
                ctx.fillText(profileData?.indexNumber || 'PENDING', textX, 85);

                // Grade & Role
                ctx.font = '10px Arial';
                ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
                ctx.fillText(`Grade ${profileData?.grade || '-'} • ${profileData?.role || 'Student'}`, textX, 105);

                // School/Location
                ctx.font = '9px Arial';
                ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
                ctx.fillText(profileData?.school_name || profileData?.city || 'Maths with Manoj', textX, 120);

                // Footer
                ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
                ctx.font = 'bold 7px Arial';
                ctx.fillText('OFFICIAL CAMPUS CARD • 2025', textX, cardHeight - 25);

                // Generate PDF
                const imgData = canvas.toDataURL('image/png');
                const pdf = new jsPDF({
                    orientation: 'landscape',
                    unit: 'in',
                    format: [4, 2]
                });
                pdf.addImage(imgData, 'PNG', 0, 0, 4, 2);
                pdf.save(`${profileData?.indexNumber || 'student'}_ID.pdf`);

                URL.revokeObjectURL(url);
            };

            qrImage.src = url;
        }
    };

    useEffect(() => {
        const loadProfile = async () => {
            try {
                const data = await fetchAPI('/common/get_profile.php');
                setProfileData(data);
                setFormData({
                    name: data.name || '',
                    email: data.email || '',
                    phone: data.phone || '',
                    birthday: data.birthday || '',
                    age: data.age || '',
                    address: data.address || '',
                    school_name: data.school_name || '',
                    grade: data.grade || '',
                    stream: data.stream || '',
                    guardian_name: data.guardian_name || '',
                    guardian_contact: data.guardian_contact || '',
                    address_line1: data.address_line1 || '',
                    address_line2: data.address_line2 || '',
                    district: data.district || '',
                    city: data.city || '',
                    institution: data.institution || '',
                    medium: data.medium || 'Sinhala'
                });

                // Fetch institutions list
                try {
                    const instData = await fetchAPI('/payment/get_institutions.php');
                    setInstitutions(instData || []);
                } catch (instErr) {
                    console.warn('Failed to load institutions list:', instErr);
                }
            } catch (err) {
                console.error('Failed to load profile:', err);
                setMessage({ type: 'error', text: 'Failed to load profile data' });
            } finally {
                setLoading(false);
            }
        };
        loadProfile();
    }, []);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handlePasswordChange = (e) => {
        setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await fetchAPI('/common/update_profile.php', {
                method: 'POST',
                body: JSON.stringify(formData)
            });
            setMessage({ type: 'success', text: 'Profile updated successfully!' });
            const data = await fetchAPI('/common/get_profile.php');
            setProfileData(data);
        } catch (err) {
            setMessage({ type: 'error', text: 'Failed to update profile: ' + err.message });
        }
        setTimeout(() => setMessage({ type: '', text: '' }), 4000);
    };

    const qrValue = encryptQR({
        i: profileData?.indexNumber || 'PENDING',
        n: profileData?.name || 'Unknown',
        uid: profileData?.id || '0',
        g: profileData?.grade || '-'
    });

    if (loading) {
        return (
            <div className="min-h-[50vh] flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto pb-10 px-4 pt-6">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row items-center gap-6 mb-8 p-6 bg-white rounded-xl shadow-sm border border-blue-100">
                <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center text-blue-600 text-3xl font-bold uppercase ring-4 ring-white shadow-sm">
                    {profileData?.name?.charAt(0)}
                </div>
                <div className="text-center md:text-left flex-1">
                    <h1 className="text-2xl font-bold text-gray-800">{profileData?.name}</h1>
                    <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 mt-2">
                        <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full uppercase">
                            {profileData?.role || 'Student'}
                        </span>
                        {profileData?.indexNumber && (
                            <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-semibold rounded-full flex items-center gap-1">
                                <FaIdCard className="text-gray-400" /> {profileData.indexNumber}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Navigation Menu (Desktop & Mobile Horizontal) */}
            <div className="mb-8 bg-white/80 backdrop-blur-md rounded-xl shadow-sm border border-blue-100 p-1.5 flex flex-wrap gap-2 sticky top-4 z-20">
                {[
                    { id: 'personal', label: 'Personal', icon: FaUserCircle },
                    { id: 'academic', label: 'Education', icon: FaGraduationCap },
                    { id: 'delivery', label: 'Logistics', icon: FaMapPin },
                    { id: 'security', label: 'Security', icon: FaLock },
                ].map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-xs md:text-sm font-bold uppercase tracking-wide transition-all duration-200 ${activeTab === tab.id
                            ? 'bg-blue-600 text-white shadow-md shadow-blue-200'
                            : 'text-gray-500 hover:bg-white hover:text-blue-600 hover:shadow-sm'
                            }`}
                    >
                        <tab.icon className={activeTab === tab.id ? 'text-white' : 'text-gray-400'} />
                        <span className="hidden md:inline">{tab.label}</span>
                        <span className="md:hidden">{tab.label.split(' ')[0]}</span>
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Left Side: Navigation & Digital ID */}
                <div className="lg:col-span-4 space-y-6">
                    {/* Digital ID Card */}
                    <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl p-6 text-white shadow-md relative overflow-hidden group">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-bl-[80px] -z-0"></div>
                        <div className="relative z-10 flex flex-col items-center">
                            <h3 className="text-xs font-bold uppercase tracking-widest mb-4 opacity-90">Digital Identity</h3>
                            <div ref={qrRef} className="p-3 bg-white rounded-lg shadow-lg mb-4">
                                <QRCodeSVG value={qrValue} size={120} level="H" fgColor="#1e3a8a" />
                            </div>
                            <div className="text-center">
                                <p className="font-bold text-lg tracking-wider">{profileData?.indexNumber || 'PENDING'}</p>
                                <p className="text-xs opacity-75 mt-1">Authorized Access</p>
                            </div>
                            <button
                                onClick={downloadIDCard}
                                className="mt-4 w-full py-2.5 bg-white/20 hover:bg-white/30 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors flex items-center justify-center gap-2"
                            >
                                <FaDownload /> Download ID
                            </button>
                        </div>
                    </div>

                </div>

                {/* Right Side: Content Area */}
                <div className="lg:col-span-8">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activeTab}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                        >
                            {/* Alert Message */}
                            {message.text && (
                                <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 text-sm font-medium ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
                                    }`}>
                                    {message.text}
                                </div>
                            )}

                            {activeTab === 'personal' && (
                                <form onSubmit={handleSubmit} className="bg-white rounded-xl p-6 md:p-8 shadow-sm border border-blue-100">
                                    <h2 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
                                        <FaUserCircle className="text-blue-600" /> Personal Details
                                    </h2>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Full Name</label>
                                            <input name="name" value={formData.name} onChange={handleChange} className="w-full bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all" />
                                        </div>
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Email (Read-Only)</label>
                                            <input name="email" value={formData.email} disabled className="w-full bg-gray-100 border border-gray-200 rounded-lg p-3 text-sm text-gray-500 cursor-not-allowed" />
                                        </div>
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">WhatsApp</label>
                                            <input name="phone" value={formData.phone} onChange={handleChange} className="w-full bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all" />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-1.5">
                                                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Birthday</label>
                                                <input name="birthday" type="date" value={formData.birthday} onChange={handleChange} className="w-full bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm focus:bg-white focus:border-blue-500 outline-none" />
                                            </div>
                                            <div className="space-y-1.5">
                                                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Age</label>
                                                <input name="age" type="number" value={formData.age} onChange={handleChange} className="w-full bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm focus:bg-white focus:border-blue-500 outline-none" />
                                            </div>
                                        </div>
                                        <div className="md:col-span-2 space-y-1.5">
                                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Quick Address</label>
                                            <textarea name="address" value={formData.address} onChange={handleChange} className="w-full bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm focus:bg-white focus:border-blue-500 outline-none min-h-[80px]" />
                                        </div>
                                    </div>
                                    <div className="mt-8 flex justify-end">
                                        <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg text-sm font-bold shadow-md shadow-blue-200 transition-all active:scale-95 flex items-center gap-2">
                                            <FaSave /> Save Changes
                                        </button>
                                    </div>
                                </form>
                            )}

                            {activeTab === 'academic' && (
                                <form onSubmit={handleSubmit} className="bg-white rounded-xl p-6 md:p-8 shadow-sm border border-blue-100">
                                    <h2 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
                                        <FaGraduationCap className="text-blue-600" /> Academic Information
                                    </h2>
                                    <div className="space-y-5">
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">School Name</label>
                                            <input name="school_name" value={formData.school_name} onChange={handleChange} className="w-full bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm focus:bg-white focus:border-blue-500 outline-none" />
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                            <div className="space-y-1.5">
                                                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Grade</label>
                                                <select name="grade" value={formData.grade} onChange={handleChange} className="w-full bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm focus:bg-white focus:border-blue-500 outline-none">
                                                    <option value="">Select Grade</option>
                                                    {[6, 7, 8, 9, 10, 11,].map(g => <option key={g} value={g}>Grade {g}</option>)}
                                                </select>
                                            </div>
                                            <div className="space-y-1.5">
                                                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Stream</label>
                                                <select name="stream" value={formData.stream} onChange={handleChange} className="w-full bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm focus:bg-white focus:border-blue-500 outline-none">
                                                    <option value="">Select Stream</option>
                                                    {['O/L ', 'A/L'].map(s => <option key={s} value={s}>{s}</option>)}
                                                </select>
                                            </div>
                                        </div>
                                        {/* Institution Dropdown */}
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Institution</label>
                                            <select
                                                name="institution"
                                                value={formData.institution}
                                                onChange={handleChange}
                                                className="w-full bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm focus:bg-white focus:border-blue-500 outline-none transition-all"
                                            >
                                                <option value="">Select Institution</option>
                                                {institutions.map(inst => (
                                                    <option key={inst.id} value={inst.name}>{inst.name} - {inst.location}</option>
                                                ))}
                                            </select>
                                        </div>
                                        {/* Medium Selection */}
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Medium</label>
                                            <select
                                                name="medium"
                                                value={formData.medium}
                                                onChange={handleChange}
                                                className="w-full bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm focus:bg-white focus:border-blue-500 outline-none transition-all"
                                            >
                                                <option value="Sinhala">Sinhala Medium</option>
                                                <option value="English">English Medium</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="mt-8 flex justify-end">
                                        <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg text-sm font-bold shadow-md shadow-blue-200 transition-all active:scale-95 flex items-center gap-2">
                                            <FaSave /> Save Changes
                                        </button>
                                    </div>
                                </form>
                            )}

                            {activeTab === 'delivery' && (
                                <form onSubmit={handleSubmit} className="bg-white rounded-xl p-6 md:p-8 shadow-sm border border-blue-100">
                                    <h2 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
                                        <FaMapPin className="text-blue-600" /> Delivery & Guardian
                                    </h2>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        <div className="md:col-span-2 space-y-1.5">
                                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Address (Full)</label>
                                            <input name="address" value={formData.address} onChange={handleChange} className="w-full bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm focus:bg-white focus:border-blue-500 outline-none" placeholder="No. 123, Street Name, City" />
                                        </div>
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">District</label>
                                            <select name="district" value={formData.district} onChange={handleChange} className="w-full bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm focus:bg-white focus:border-blue-500 outline-none">
                                                <option value="">Select District</option>
                                                {['Colombo', 'Gampaha', 'Kalutara', 'Kandy', 'Matale', 'Nuwara Eliya', 'Galle', 'Matara', 'Hambantota', 'Jaffna', 'Kilinochchi', 'Mannar', 'Vavuniya', 'Mullaitivu', 'Batticaloa', 'Ampara', 'Trincomalee', 'Kurunegala', 'Puttalam', 'Anuradhapura', 'Polonnaruwa', 'Badulla', 'Monaragala', 'Ratnapura', 'Kegalle'].map(d => <option key={d} value={d}>{d}</option>)}
                                            </select>
                                        </div>
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">City</label>
                                            <input name="city" value={formData.city} onChange={handleChange} className="w-full bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm focus:bg-white focus:border-blue-500 outline-none" />
                                        </div>
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Guardian Name</label>
                                            <input name="guardian_name" value={formData.guardian_name} onChange={handleChange} className="w-full bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm focus:bg-white focus:border-blue-500 outline-none" />
                                        </div>
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Guardian Phone</label>
                                            <input name="guardian_contact" value={formData.guardian_contact} onChange={handleChange} className="w-full bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm focus:bg-white focus:border-blue-500 outline-none" />
                                        </div>
                                    </div>
                                    <div className="mt-8 flex justify-end">
                                        <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg text-sm font-bold shadow-md shadow-blue-200 transition-all active:scale-95 flex items-center gap-2">
                                            <FaSave /> Save Changes
                                        </button>
                                    </div>
                                </form>
                            )}

                            {activeTab === 'security' && (
                                <form
                                    onSubmit={async (e) => {
                                        e.preventDefault();
                                        if (passwordData.new !== passwordData.confirm) return setMessage({ type: 'error', text: 'New passwords do not match' });
                                        if (!validatePassword(passwordData.new).isValid) return setMessage({ type: 'error', text: 'Password weak' });
                                        try {
                                            const res = await changePassword(passwordData.current, passwordData.new);
                                            setMessage({ type: 'success', text: res.message });
                                            setPasswordData({ current: '', new: '', confirm: '' });
                                        } catch (err) { setMessage({ type: 'error', text: err.message }); }
                                    }}
                                    className="bg-white rounded-xl p-6 md:p-8 shadow-sm border border-blue-100"
                                >
                                    <h2 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
                                        <FaLock className="text-blue-600" /> Security Settings
                                    </h2>
                                    <div className="space-y-5 max-w-lg">
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Current Password</label>
                                            <input type="password" name="current" value={passwordData.current} onChange={handlePasswordChange} className="w-full bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm focus:bg-white focus:border-blue-500 outline-none" required />
                                        </div>
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">New Password</label>
                                            <input type="password" name="new" value={passwordData.new} onChange={handlePasswordChange} className="w-full bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm focus:bg-white focus:border-blue-500 outline-none" required />
                                            <PasswordStrengthIndicator password={passwordData.new} showRequirements={true} />
                                        </div>
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Confirm New</label>
                                            <input type="password" name="confirm" value={passwordData.confirm} onChange={handlePasswordChange} className="w-full bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm focus:bg-white focus:border-blue-500 outline-none" required />
                                        </div>
                                    </div>
                                    <div className="mt-8 flex justify-end">
                                        <button type="submit" className="bg-red-500 hover:bg-red-600 text-white px-6 py-2.5 rounded-lg text-sm font-bold shadow-md shadow-red-200 transition-all active:scale-95 flex items-center gap-2">
                                            <FaKey /> Update Password
                                        </button>
                                    </div>
                                </form>
                            )}
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
}
